import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { format, differenceInCalendarDays } from "date-fns";
import { ArrowLeft, MapPin, BadgeCheck, ShieldCheck, Truck, MessageSquare, Calendar as CalendarIcon, Heart, ExternalLink, Globe, Instagram, Facebook, Music2, Crown, Smartphone } from "lucide-react";

const CONTACT_DEFS = [
  { key: "website", label: "Website", Icon: Globe },
  { key: "whatsapp", label: "WhatsApp", Icon: MessageSquare },
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "tiktok", label: "TikTok", Icon: Music2 },
];

function normalizeLink(key, raw) {
  if (!raw) return null;
  const v = String(raw).trim();
  if (!v) return null;
  if (key === "whatsapp") {
    if (/^https?:\/\//i.test(v)) return v;
    const digits = v.replace(/\D+/g, "");
    return digits ? `https://wa.me/${digits}` : null;
  }
  if (key === "instagram") {
    if (/^https?:\/\//i.test(v)) return v;
    const handle = v.replace(/^@/, "");
    return `https://instagram.com/${handle}`;
  }
  if (key === "tiktok") {
    if (/^https?:\/\//i.test(v)) return v;
    const handle = v.replace(/^@/, "");
    return `https://tiktok.com/@${handle}`;
  }
  if (key === "facebook") {
    if (/^https?:\/\//i.test(v)) return v;
    return `https://facebook.com/${v.replace(/^@/, "")}`;
  }
  // website
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}
import { api } from "@/lib/api";
import { useApp } from "@/store";
import { Calendar } from "@/components/ui/calendar";
import { Stars } from "@/components/Stars";
import ShareButtons from "@/components/ShareButtons";
import { ReportButton } from "@/components/ReportButton";

const FLAG = { BR: "🇧🇷 Brazil", AU: "🇦🇺 Australia", US: "🇺🇸 United States" };

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, priceDisplay, formatMoney, currency, lang, user, favorites, toggleFavorite } = useApp();
  const [item, setItem] = useState(null);
  const [tr, setTr] = useState(null);
  const [active, setActive] = useState(0);
  const [range, setRange] = useState();
  const [busy, setBusy] = useState([]);
  const [reviews, setReviews] = useState({ reviews: [], avg: null, count: 0 });
  const [ownerRating, setOwnerRating] = useState(null);
  const [promoPrices, setPromoPrices] = useState(null);
  useEffect(() => {
    if (item && user?.user_id === item.owner_id) api.get("/promo-prices").then(({ data }) => setPromoPrices(data.prices?.[item.currency] || data.prices?.AUD)).catch(() => {});
  }, [user, item]);

  const loadExtras = () => {
    api.get(`/listings/${id}/reviews`).then(({ data }) => setReviews(data)).catch(() => {});
    api.get(`/listings/${id}/availability`).then(({ data }) => setBusy(data)).catch(() => {});
  };
  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setItem(data)).catch(() => setItem(false));
    loadExtras();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (item && item.owner_id) api.get(`/users/${item.owner_id}/rating`).then(({ data }) => setOwnerRating(data)).catch(() => {});
  }, [item]);

  const days = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) + 1 : 0;
  const total = (() => {
    if (!days || !item) return 0;
    const ppd = item.price_per_day || 0, ppw = item.price_per_week;
    if (ppw && days >= 7) { const w = Math.floor(days / 7), r = days % 7; return w * ppw + r * ppd; }
    return days * ppd;
  })();

  const requestBooking = async () => {
    if (!user) { navigate("/login"); return; }
    if (!range?.from || !range?.to) { toast.error("Please select dates"); return; }
    try {
      await api.post("/bookings", { listing_id: item.listing_id, start_date: format(range.from, "yyyy-MM-dd"), end_date: format(range.to, "yyyy-MM-dd") });
      toast.success("Booking requested!");
      navigate("/bookings");
    } catch (e) { toast.error(e.response?.data?.detail || "Booking failed"); }
  };


  const contactOwner = async () => {
    if (!user) { navigate("/login"); return; }
    if (user.user_id === item.owner_id) { toast.info("This is your own listing"); return; }
    try {
      const { data } = await api.post("/conversations", { listing_id: item.listing_id });
      navigate(`/messages?c=${data.conversation_id}`);
    } catch { toast.error("Could not start conversation"); }
  };

  if (item === null) return <div className="py-24 text-center text-gray-400">Loading…</div>;
  if (item === false) return <div className="py-24 text-center text-gray-400">Listing not found.</div>;

  const photos = item.photos?.length ? item.photos : [item.image_url];
  const disabledDays = [{ before: new Date() }, ...busy.map((b) => ({ from: new Date(b.start_date + "T00:00"), to: new Date(b.end_date + "T00:00") }))];
  const saved = favorites?.has(item.listing_id);
  const toggleSave = () => { if (!user) { navigate("/login"); return; } toggleFavorite(item.listing_id); };
  const doTranslate = async () => {
    if (tr) { setTr(null); return; }
    try {
      const [a, b] = await Promise.all([
        api.post("/translate", { text: item.title, target_lang: lang }),
        api.post("/translate", { text: item.description, target_lang: lang }),
      ]);
      setTr({ title: a.data.text, description: b.data.text });
    } catch { toast.error("Translation failed"); }
  };
  const setAlert = async () => {
    if (!user) { navigate("/login"); return; }
    if (!range?.from || !range?.to) { toast.error("Select dates first"); return; }
    try { await api.post("/alerts", { listing_id: item.listing_id, start_date: format(range.from, "yyyy-MM-dd"), end_date: format(range.to, "yyyy-MM-dd") }); toast.success("We'll notify you when these dates open up"); }
    catch { toast.error("Could not set alert"); }
  };
  const promote = async (pkg) => {
    try {
      const { data } = await api.post("/payments/checkout", { listing_id: item.listing_id, package: pkg, currency: item.currency, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); }
  };

  return (
    <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 transition-colors hover:text-[#002FA7]"><ArrowLeft size={16} /> Back</button>
      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <div className="overflow-hidden rounded-3xl border border-gray-200 bg-gray-100">
            <img src={photos[active]} alt={item.title} className="aspect-[4/3] w-full object-cover" data-testid="detail-image" />
          </div>
          {photos.length > 1 && (
            <div className="mt-3 flex gap-2" data-testid="detail-thumbs">
              {photos.map((p, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                  className={`h-16 w-16 overflow-hidden rounded-xl border-2 transition-colors ${active === i ? "border-[#002FA7]" : "border-transparent"}`}>
                  <img src={p} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-gray-400">{item.category}</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl" data-testid="detail-title">{tr?.title || item.title}</h1>
          <button data-testid="translate-btn" onClick={doTranslate} className="mt-1 text-xs font-bold text-[#002FA7] hover:underline">{tr ? "Show original" : "🌐 Translate"}</button>
          <div className="mt-3 flex items-center gap-2 text-gray-500"><MapPin size={16} /> {item.city ? `${item.city}, ` : ""}{FLAG[item.country]}{item.distance_km != null && ` · ${item.distance_km} km away`}</div>

          <div className="mt-6 rounded-2xl border border-gray-200 p-6">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-4xl font-extrabold text-[#002FA7]" data-testid="detail-price">{priceDisplay(item.price_per_day, item.currency)}</span>
              <span className="text-gray-400">{t("per_day")}</span>
            </div>
            {item.price_per_week && (
              <div className="mt-1 font-semibold text-gray-700" data-testid="detail-price-week">{priceDisplay(item.price_per_week, item.currency)} <span className="font-normal text-gray-400">{t("per_week")}</span></div>
            )}
            <div className="mt-1 text-sm text-gray-400">
              Listed in {formatMoney(item.price_per_day, item.currency)} {item.currency}
              {item.currency !== currency && <> · shown in {currency} at today's rate</>}
            </div>
            <button data-testid="contact-owner-btn" onClick={contactOwner} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FF3B30] py-3.5 font-bold text-white transition-colors hover:bg-[#E6352B]"><MessageSquare size={18} /> {t("contact_owner")}</button>
            <button data-testid="save-listing-btn" onClick={toggleSave} className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border border-gray-300 py-2.5 font-bold text-gray-700 transition-colors hover:border-[#FF3B30] hover:text-[#FF3B30]"><Heart size={16} className={saved ? "fill-[#FF3B30] text-[#FF3B30]" : ""} /> {saved ? "Saved" : "Save item"}</button>
          </div>

          <div className="mt-6 rounded-2xl border border-gray-200 p-6" data-testid="booking-widget">
            <div className="mb-3 flex items-center gap-2 font-display text-lg font-bold"><CalendarIcon size={18} className="text-[#002FA7]" /> {t("book_dates")}</div>
            <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} disabled={disabledDays} />
            {days > 0 && (
              <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
                <span className="text-sm text-gray-500">{days} day(s) · {t("total")}</span>
                <span data-testid="booking-total" className="font-display text-xl font-extrabold text-[#002FA7]">{priceDisplay(total, item.currency)}</span>
              </div>
            )}
            <button data-testid="book-now-btn" onClick={requestBooking} className="mt-4 w-full rounded-full bg-[#002FA7] py-3 font-bold text-white transition-colors hover:bg-[#002280]">{t("book_now")}</button>
            <button data-testid="notify-btn" onClick={setAlert} className="mt-2 w-full rounded-full border border-gray-300 py-2 text-sm font-bold text-gray-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]">🔔 Notify me when these dates are free</button>
          </div>

          {item.delivery_terms && (
            <div className="mt-6 rounded-2xl border border-gray-200 p-5" data-testid="detail-delivery">
              <div className="mb-1 flex items-center gap-2 font-bold text-gray-900"><Truck size={16} className="text-[#002FA7]" /> {t("delivery_terms")}</div>
              <p className="text-sm leading-relaxed text-gray-600">{item.delivery_terms}</p>
            </div>
          )}

          {item.payment_terms && (
            <div className="mt-6 rounded-2xl border border-gray-200 p-5" data-testid="detail-payment-terms">
              <div className="mb-1 flex items-center gap-2 font-bold text-gray-900"><ShieldCheck size={16} className="text-[#002FA7]" /> Payment & deposit terms</div>
              <p className="text-sm leading-relaxed text-gray-600">{item.payment_terms}</p>
            </div>
          )}

          {item.contact_links && Object.values(item.contact_links).some(Boolean) && (
            <div className="mt-6 rounded-2xl border border-gray-200 p-5" data-testid="detail-contact-links">
              <div className="mb-3 flex items-center gap-2 font-bold text-gray-900"><ExternalLink size={16} className="text-[#002FA7]" /> Contact & links</div>
              <div className="flex flex-wrap gap-2">
                {CONTACT_DEFS.map(({ key, label, Icon }) => {
                  const href = normalizeLink(key, item.contact_links?.[key]);
                  if (!href) return null;
                  return <a key={key} data-testid={`contact-${key}`} href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-4 py-2 text-sm font-bold text-gray-700 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]"><Icon size={15} /> {label}</a>;
                })}
              </div>
            </div>
          )}

          <div className="mt-4 rounded-xl bg-gray-50 p-3 text-xs text-gray-500" data-testid="offplatform-note">
            RentGlobe only connects you with the advertiser. All payments, deposits and bonds are arranged directly between you and them, under their own terms.
          </div>

          <div className="mt-6 flex items-center gap-3 rounded-2xl border border-gray-200 p-4">
            <div className="grid h-11 w-11 place-items-center rounded-full bg-[#002FA7]/10 text-[#002FA7] font-display font-bold">{(item.owner_name || "?")[0]}</div>
            <div>
              <div className="flex items-center gap-1.5 font-bold text-gray-900">
                <Link to={`/u/${item.owner_id}`} data-testid="owner-link" className="hover:text-[#002FA7] hover:underline">{item.owner_name}</Link>
                {item.owner_kyc_status === "approved" && <BadgeCheck size={16} className="text-[#002FA7]" />}
                {item.owner_phone_verified && <span data-testid="detail-phone-verified" className="inline-flex items-center gap-1 rounded-full bg-[#155724]/10 px-2 py-0.5 text-[10px] font-bold text-[#155724]"><Smartphone size={11} /> {t("phone_verified")}</span>}
                {item.owner_is_pro && <span data-testid="detail-pro-badge" className="inline-flex items-center gap-1 rounded-full bg-[#0A0B10] px-2 py-0.5 text-[10px] font-bold text-yellow-300"><Crown size={11} /> {t("pro_business")}</span>}
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500"><ShieldCheck size={12} /> {item.owner_kyc_status === "approved" ? t("verified_owner") : "Identity under review"}</div>
              {ownerRating?.avg != null && <div className="mt-1 flex items-center gap-1"><Stars value={ownerRating.avg} size={13} /><span className="text-xs text-gray-500">{ownerRating.avg} ({ownerRating.count})</span></div>}
            </div>
          </div>

          <p className="mt-6 leading-relaxed text-gray-600" data-testid="detail-description">{tr?.description || item.description}</p>

          <ShareButtons url={`${process.env.REACT_APP_BACKEND_URL}/api/share/listing/${item.listing_id}`} text={`Rent "${item.title}" on RentGlobe`} />

          {user?.user_id === item.owner_id && (
            <div className="mt-6 rounded-2xl border-2 border-[#FF3B30]/30 bg-[#FF3B30]/5 p-5" data-testid="promote-box">
              <div className="mb-1 font-display font-bold text-gray-900">🚀 {t("promote")}{item.is_promoted ? ` — ${t("featured")}` : ""}</div>
              <p className="text-sm text-gray-500">Feature this item at the top of search results in {item.country}.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["7", "15", "30"].map((pkg) => (
                  <button key={pkg} data-testid={`promote-${pkg}`} onClick={() => promote(pkg)} className="rounded-full bg-[#FF3B30] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#E6352B]">{pkg} days{promoPrices?.[pkg] != null ? ` · ${item.currency} ${promoPrices[pkg].toFixed(2)}` : ""}</button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4"><ReportButton listingId={item.listing_id} testPrefix="report-listing" /></div>

          <div className="mt-8" data-testid="reviews-section">
            <div className="mb-3 flex items-center gap-3">
              <h2 className="font-display text-xl font-bold">{t("reviews")}</h2>
              {reviews.avg != null && (
                <div className="flex items-center gap-1.5"><Stars value={reviews.avg} /><span className="text-sm font-bold text-gray-700">{reviews.avg}</span><span className="text-sm text-gray-400">({reviews.count})</span></div>
              )}
            </div>
            {reviews.reviews.length === 0 ? (
              <p className="text-sm text-gray-400" data-testid="reviews-empty">{t("no_reviews")}</p>
            ) : (
              <div className="space-y-3" data-testid="reviews-list">
                {reviews.reviews.map((r) => (
                  <div key={r.review_id} className="rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between"><span className="font-bold text-gray-900">{r.reviewer_name}</span><Stars value={r.rating} size={14} /></div>
                    {r.comment && <p className="mt-1 text-sm text-gray-600">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
